import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Shield, ArrowRight, Menu, X, FileText,
  Trophy, CheckCircle,
  ChevronDown, ChevronRight, Lock, RefreshCw,
  Clock, Flame, Star, Phone, Mail,
  Users, Newspaper, Zap, Loader2,
  Anchor, Plane, Swords, Atom, Waves, Medal,
  Target, Smartphone, Flag, BookOpen, Bot, User,
  BarChart2, NotebookPen, TrendingUp, Globe,
} from "lucide-react";
import caApi from "../api/ca";
import gkApi from "../api/gk";
import examDatesApi from "../api/examDates";
import SEOHead from "../components/SEOHead";

function FadeIn({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  return (
    <motion.div ref={ref}
      initial={{ opacity: 0, y: 14 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}>
      {children}
    </motion.div>
  );
}

/* Section heading — newspaper style */
function SectionHead({ label, title, color = "bg-blue-800" }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <div className={`w-1.5 h-7 rounded-full ${color}`} />
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-gray-400">{label}</p>
        <h2 className="font-sora font-bold text-xl text-gray-900 leading-tight">{title}</h2>
      </div>
    </div>
  );
}

/* ── DATA ────────────────────────────────────────────────────── */
const CA_CAT_STYLE = {
  "Defence":             { cardBg: "#F2F6EA", border: "#4A5E2A", Icon: Shield,     label: "Defence" },
  "National":            { cardBg: "#FEF3C7", border: "#D97706", Icon: Flag,       label: "National" },
  "International":       { cardBg: "#EFF6FF", border: "#1D4ED8", Icon: Globe,      label: "International" },
  "Economy":             { cardBg: "#ECFDF5", border: "#059669", Icon: TrendingUp, label: "Economy" },
  "Science & Technology":{ cardBg: "#F5F3FF", border: "#7C3AED", Icon: Atom,       label: "Science & Tech" },
  "Sports":              { cardBg: "#FDF2F8", border: "#DB2777", Icon: Trophy,      label: "Sports" },
  "Awards & Honours":    { cardBg: "#FFFBEB", border: "#D97706", Icon: Medal,       label: "Awards" },
  "Appointments":        { cardBg: "#F5F3FF", border: "#7C3AED", Icon: Users,       label: "Appointments" },
  "Government Schemes":  { cardBg: "#EFF6FF", border: "#2563EB", Icon: BookOpen,    label: "Schemes" },
  "Environment":         { cardBg: "#F0FDF4", border: "#16A34A", Icon: Waves,       label: "Environment" },
  "default":             { cardBg: "#F9FAFB", border: "#6B7280", Icon: Newspaper,   label: "News" },
};
function getCaStyle(cat) { return CA_CAT_STYLE[cat] || CA_CAT_STYLE.default; }

const DIFF_NUM_CLS = {
  easy:   "bg-green-100 text-green-700",
  medium: "bg-yellow-100 text-yellow-700",
  hard:   "bg-red-100 text-red-700",
};


const PLANS = [
  {
    name: "Free", price: 0, desc: "Always free — no credit card ever",
    inc: [
      "Daily Current Affairs — full digest, forever free",
      "Complete GK Library (NCERT + Lucent)",
      "5 full-length mock tests per month",
      "Hindi + English",
    ],
    exc: [
      "Specialised exam notes (NDA/CDS/AFCAT)",
      "Unlimited mock tests + All India Rank",
      "10 years PYQ papers with solutions",
      "SSB Psychology prep (TAT/WAT/GTO)",
    ],
    cta: "Start Free — No Card", hl: false, badge: null,
  },
  {
    name: "Aspirant", price: 49, annual: 399, desc: "For aspirants serious about clearing in 1 attempt",
    inc: [
      "Everything in Free",
      "Specialised exam notes — NDA, CDS, AFCAT, CAPF",
      "Unlimited mock tests + All India Rank",
      "10 years PYQ papers with detailed solutions",
      "SSB prep — TAT, WAT, SRT, GTO, PI",
      "Performance analytics & weak-area reports",
      "Offline mobile access",
      "Unlimited AI doubt solving",
    ],
    exc: [],
    cta: "Start 7-Day Free Trial", hl: true, badge: "Most Popular",
  },
  {
    name: "Coaching Centre", price: 2999, desc: "Built for NDA/CDS coaching institutes",
    inc: [
      "Everything in Aspirant",
      "Up to 200 student accounts",
      "Batch & mentor management",
      "Custom test creation for batches",
      "Batch-level analytics & reports",
      "Branded student portal",
    ],
    exc: [],
    cta: "Book a Demo", hl: false, badge: "For Institutes",
  },
];

const FAQS = [
  { q: "Is Current Affairs really free — forever?", a: "Yes. Daily CA digest is free for every registered user, forever. No trial period, no credit card. We believe access to news should never be paywalled for a defence aspirant." },
  { q: "Is the GK Library completely free?", a: "Completely free. All NCERT-based GK topics, key facts, and the practice MCQs in the GK Library are free. Our philosophy: foundational knowledge should be accessible to everyone." },
  { q: "What exactly is paid then?", a: "Paid plans unlock: (1) Specialised exam notes written by ex-servicemen and educators — chapter-wise for NDA Maths, CDS English, AFCAT Reasoning, etc. (2) Unlimited full mock tests with All India Rank. (3) 10 years PYQ papers with solutions. (4) SSB psychology prep." },
  { q: "Is this platform only for NDA?", a: "No. We cover NDA, CDS (IMA/OTA/AFA/NA), AFCAT, CAPF (AC), Territorial Army, MNS and SSB. Select your target exam at signup — content is tailored accordingly." },
  { q: "What makes the mock tests worth paying for?", a: "Our mock tests are exam-pattern exact — same marks, same negative marking, same time. After each test you get an All India Rank among all aspirants who took that test, plus a weak-area report showing exactly which topics to focus on next." },
  { q: "Is Hindi medium content available?", a: "Yes. Full platform in Hindi and English — CA, GK notes, specialized notes, and mock tests all available in both languages. Switch anytime from your profile settings." },
  { q: "Can I access it on mobile?", a: "Yes. Android + iOS app with offline mode. Download notes and practice without internet — essential for aspirants in areas with poor connectivity." },
  { q: "Can I cancel anytime?", a: "Yes. No lock-in, no questions asked. Cancel from account settings — you won't be charged again. Free features (CA + GK) remain accessible even after cancellation." },
];

const WHY = [
  "Daily CA digest at 6 AM — free forever",
  "Complete GK Library — free forever",
  "Specialised notes: NDA, CDS, AFCAT, CAPF",
  "Unlimited mock tests + All India Rank",
  "10 years PYQ papers — fully solved",
  "SSB prep: TAT, WAT, GTO, PI",
  "Hindi + English — switch anytime",
  "Only ₹49/month for paid features",
];

/* ── Daily popup data (rotates every day) ───────────────────────────────── */
const DAILY = [
  { type: "fact",  icon: Shield,    label: "Defence Fact",        text: "India's NDA in Khadakwasla is the world's first tri-service academy, training future officers of the Army, Navy and Air Force together." },
  { type: "quote", icon: Star,      label: "Quote of the Day",    text: "The more you sweat in peace, the less you bleed in war.", author: "Gen. Norman Schwarzkopf" },
  { type: "fact",  icon: Anchor,    label: "Defence Fact",        text: "INS Vikrant (IAC-1), commissioned in September 2022, is India's first indigenously designed and built aircraft carrier." },
  { type: "quote", icon: Flame,     label: "Quote of the Day",    text: "Either I will find a way, or I will make one.", author: "Philip Sidney" },
  { type: "fact",  icon: Swords,    label: "Defence Fact",        text: "India has the world's highest battlefield — the Siachen Glacier at ~6,000 m above sea level, guarded by the Indian Army year-round." },
  { type: "quote", icon: Target,    label: "Quote of the Day",    text: "Don't watch the clock; do what it does. Keep going.", author: "Sam Levenson" },
  { type: "fact",  icon: Plane,     label: "Defence Fact",        text: "Mission Shakti (March 2019) made India only the 4th country in the world — after USA, Russia and China — to demonstrate ASAT (Anti-Satellite) capability." },
  { type: "quote", icon: Zap,       label: "Quote of the Day",    text: "If you're going through hell, keep going.", author: "Winston Churchill" },
  { type: "fact",  icon: Trophy,    label: "Defence Fact",        text: "The Param Vir Chakra has been awarded only 21 times since Independence — 14 of them posthumously, for the ultimate acts of valour." },
  { type: "quote", icon: Star,      label: "Quote of the Day",    text: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
  { type: "fact",  icon: Globe,     label: "Defence Fact",        text: "India conducts bilateral military exercises with 40+ countries annually — from Yudh Abhyas (USA) to Shakti (France) and Malabar (USA, Japan, Australia)." },
  { type: "quote", icon: Shield,    label: "Quote of the Day",    text: "The Indian soldier's indomitable spirit has never failed his country.", author: "Field Marshal Sam Manekshaw" },
  { type: "fact",  icon: Atom,      label: "Defence Fact",        text: "India's nuclear doctrine, announced in 2003, is built on No First Use (NFU) and Minimum Credible Deterrence — it will never be the first to use nuclear weapons." },
  { type: "quote", icon: Flame,     label: "Quote of the Day",    text: "Do not pray for easy lives. Pray to be stronger men.", author: "John F. Kennedy" },
  { type: "fact",  icon: Waves,     label: "Defence Fact",        text: "INS Arihant, commissioned in 2016, is India's first indigenously built nuclear-powered ballistic missile submarine (SSBN), completing the nuclear triad." },
  { type: "quote", icon: Zap,       label: "Quote of the Day",    text: "Courage is not the absence of fear, but the judgement that something else is more important than fear.", author: "Ambrose Redmoon" },
  { type: "fact",  icon: Swords,    label: "Defence Fact",        text: "Operation Meghdoot (1984) gave India control over the Siachen Glacier — the highest militarized zone on Earth — in a bloodless but strategically vital mission." },
  { type: "quote", icon: Target,    label: "Quote of the Day",    text: "A dream doesn't become reality through magic; it takes sweat, determination and hard work.", author: "Colin Powell" },
  { type: "fact",  icon: Medal,     label: "Defence Fact",        text: "The Indian Army is one of the world's largest volunteer armies with over 1.4 million active-duty personnel, and has never used its power against its own citizens." },
  { type: "quote", icon: Star,      label: "Quote of the Day",    text: "We are Indians, firstly and lastly.", author: "Dr. B. R. Ambedkar" },
  { type: "fact",  icon: Plane,     label: "Defence Fact",        text: "The IAF's Rafale jets, inducted in 2020, carry the METEOR BVR missile — effective at ranges beyond 150 km, giving IAF a decisive edge in aerial combat." },
  { type: "quote", icon: Shield,    label: "Quote of the Day",    text: "The secret of getting ahead is getting started.", author: "Mark Twain" },
  { type: "fact",  icon: Anchor,    label: "Defence Fact",        text: "India's coastline stretches 7,516.6 km. The Indian Navy's three Commands — Western (Mumbai), Eastern (Visakhapatnam) and Southern (Kochi) — guard every metre." },
  { type: "quote", icon: Flame,     label: "Quote of the Day",    text: "It does not matter how slowly you go as long as you do not stop.", author: "Confucius" },
  { type: "fact",  icon: Trophy,    label: "Defence Fact",        text: "India's DRDO has developed the BrahMos — the world's fastest operational supersonic cruise missile at Mach 2.8–3.0 — in collaboration with Russia." },
  { type: "quote", icon: Zap,       label: "Quote of the Day",    text: "You are never too old to set another goal or to dream a new dream.", author: "C. S. Lewis" },
  { type: "fact",  icon: Globe,     label: "Defence Fact",        text: "Chandrayaan-3's Vikram lander touched down on the Moon's south pole on 23 August 2023, making India the first country in the world to achieve this." },
  { type: "quote", icon: Target,    label: "Quote of the Day",    text: "The strength of the Army lies not in weapons, but in the minds of men.", author: "Gen. Douglas MacArthur" },
  { type: "fact",  icon: Atom,      label: "Defence Fact",        text: "The Agni-V missile, with a range exceeding 5,000 km, can reach targets across Asia and parts of Europe — India's most potent strategic deterrent." },
  { type: "quote", icon: Star,      label: "Quote of the Day",    text: "In the middle of difficulty lies opportunity.", author: "Albert Einstein" },
];

function getDailyCard() {
  const start = new Date(new Date().getFullYear(), 0, 0);
  const dayOfYear = Math.floor((Date.now() - start) / 86400000);
  return DAILY[dayOfYear % DAILY.length];
}

function DailyPopup({ onClose }) {
  const navigate = useNavigate();
  const card = getDailyCard();
  const CardIcon = card.icon;
  const isQuote = card.type === "quote";

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: "rgba(10,15,10,0.75)", backdropFilter: "blur(6px)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.88, y: 24 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.92, y: 16 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative w-full max-w-md rounded-3xl overflow-hidden shadow-2xl"
        style={{ background: "linear-gradient(160deg,#1A2810 0%,#243018 60%,#1E2D12 100%)" }}>

        {/* Gold top bar */}
        <div className="h-1" style={{ background: "linear-gradient(90deg,#C9A227,#E8C84A,#C9A227)" }} />

        {/* Close */}
        <button onClick={onClose}
          className="absolute top-4 right-4 w-7 h-7 rounded-full flex items-center justify-center transition-all"
          style={{ background: "rgba(255,255,255,0.08)" }}>
          <X size={14} style={{ color: "#A8B89A" }} />
        </button>

        <div className="px-7 pt-7 pb-6">
          {/* Label */}
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center"
              style={{ background: "rgba(201,162,39,0.18)", border: "1px solid rgba(201,162,39,0.3)" }}>
              <CardIcon size={16} style={{ color: "#C9A227" }} />
            </div>
            <span className="text-[11px] font-extrabold uppercase tracking-widest" style={{ color: "#C9A227" }}>
              {card.label}
            </span>
            <span className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full"
              style={{ background: "rgba(201,162,39,0.12)", color: "#9A8050" }}>
              {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
            </span>
          </div>

          {/* Content */}
          {isQuote ? (
            <div>
              <div className="text-4xl font-serif mb-3" style={{ color: "rgba(201,162,39,0.4)", lineHeight: 1 }}>"</div>
              <p className="font-sora font-bold text-xl text-white leading-snug mb-3" style={{ textIndent: "-0.5rem", paddingLeft: "0.5rem" }}>
                {card.text}
              </p>
              <p className="text-sm font-semibold" style={{ color: "#C9A227" }}>— {card.author}</p>
            </div>
          ) : (
            <div>
              <div className="w-full h-px mb-5" style={{ background: "rgba(201,162,39,0.2)" }} />
              <p className="font-sora font-semibold text-lg text-white leading-relaxed">
                {card.text}
              </p>
            </div>
          )}

          {/* Bottom bar */}
          <div className="flex items-center gap-3 mt-7 pt-5" style={{ borderTop: "1px solid rgba(201,162,39,0.15)" }}>
            <button onClick={() => { onClose(); navigate("/login"); }}
              className="flex-1 py-2.5 rounded-xl text-sm font-extrabold transition-all"
              style={{ background: "#C9A227", color: "#1A2810" }}>
              Start Preparing Free →
            </button>
            <button onClick={onClose}
              className="py-2.5 px-4 rounded-xl text-sm font-semibold transition-all"
              style={{ background: "rgba(255,255,255,0.07)", color: "#A8B89A", border: "1px solid rgba(255,255,255,0.1)" }}>
              Close
            </button>
          </div>
          <p className="text-center text-[10px] mt-3" style={{ color: "#4A5E3A" }}>
            New {isQuote ? "quote" : "fact"} every day · Close to read today's news
          </p>
        </div>
      </motion.div>
    </motion.div>
  );
}

/* ─── Exam Countdown Card ─────────────────────────────────────── */
const UPCOMING_EXAMS = [
  { name: "AFCAT II", date: new Date("2026-08-08"), color: "#FBD26A", bg: "rgba(201,162,39,0.35)", icon: Plane  },
  { name: "NDA II",   date: new Date("2026-09-14"), color: "#A8C87A", bg: "rgba(74,94,42,0.45)",   icon: Swords },
  { name: "TA",       date: new Date("2026-07-12"), color: "#F9A87A", bg: "rgba(180,72,40,0.35)",  icon: Shield },
  { name: "CDS II",   date: new Date("2026-11-22"), color: "#93B8F8", bg: "rgba(29,78,216,0.35)",  icon: Flag   },
  { name: "NDA I",    date: new Date("2027-04-18"), color: "#C4B5FD", bg: "rgba(109,40,217,0.35)", icon: Swords },
];

function daysUntil(date) {
  const diff = date - new Date();
  return Math.max(0, Math.ceil(diff / 86400000));
}

function urgencyColor(days) {
  if (days <= 30) return "#F87171";
  if (days <= 75) return "#FBBF24";
  return "#86EFAC";
}

function ExamCountdownCard({ navigate }) {
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick(t => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const sorted = [...UPCOMING_EXAMS]
    .map(e => ({ ...e, days: daysUntil(e.date) }))
    .filter(e => e.days > 0)
    .sort((a, b) => a.days - b.days);

  const nearest = sorted[0];

  return (
    <div className="rounded-2xl overflow-hidden shadow-2xl"
      style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(201,162,39,0.25)" }}>

      {/* Header */}
      <div className="px-4 py-2.5 flex items-center justify-between"
        style={{ background: "linear-gradient(90deg,#C9A227,#B8920F)" }}>
        <div className="flex items-center gap-2">
          <Clock size={13} style={{ color: "#1A2810" }} />
          <span className="text-xs font-extrabold uppercase tracking-wide" style={{ color: "#1A2810" }}>Exam Countdown</span>
        </div>
        <span className="text-[11px] font-semibold" style={{ color: "#2D3B1F" }}>
          {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
        </span>
      </div>

      {/* Nearest exam — big highlight */}
      {nearest && (
        <div className="px-4 pt-4 pb-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
          <p className="text-[10px] font-bold uppercase tracking-widest mb-1.5" style={{ color: "#C9A227" }}>
            Next Exam
          </p>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white text-base font-extrabold leading-tight">{nearest.name} 2026</p>
              <p className="text-[11px] mt-0.5" style={{ color: "#8A9E78" }}>
                {nearest.date.toLocaleDateString("en-IN", { day: "numeric", month: "long" })}
              </p>
            </div>
            <div className="text-right">
              <p className="font-extrabold text-2xl leading-none" style={{ color: urgencyColor(nearest.days) }}>
                {nearest.days}
              </p>
              <p className="text-[10px] font-bold uppercase tracking-wide" style={{ color: "#7A9160" }}>days left</p>
            </div>
          </div>
          {/* Progress bar */}
          <div className="mt-2.5 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.08)" }}>
            <div className="h-full rounded-full transition-all"
              style={{
                width: `${Math.min(100, Math.max(4, 100 - (nearest.days / 180) * 100))}%`,
                background: urgencyColor(nearest.days),
              }} />
          </div>
        </div>
      )}

      {/* Other exams — compact grid */}
      <div className="grid grid-cols-3 divide-x" style={{ divideColor: "rgba(255,255,255,0.06)" }}>
        {sorted.slice(1, 4).map((exam) => {
          const Icon = exam.icon;
          return (
            <div key={exam.name} className="px-3 py-3 text-center">
              <div className="flex items-center justify-center mb-1">
                <span className="text-[9px] font-extrabold uppercase tracking-wide" style={{ color: exam.color }}>{exam.name}</span>
              </div>
              <p className="font-extrabold text-lg leading-none" style={{ color: urgencyColor(exam.days) }}>{exam.days}</p>
              <p className="text-[9px] mt-0.5 font-semibold" style={{ color: "#5A7050" }}>days</p>
            </div>
          );
        })}
      </div>

      {/* Platform stats */}
      <div className="grid grid-cols-3 px-4 py-3 gap-2"
        style={{ borderTop: "1px solid rgba(201,162,39,0.15)", background: "rgba(0,0,0,0.15)" }}>
        {[
          { label: "Aspirants", value: "10,000+" },
          { label: "Questions", value: "50,000+" },
          { label: "CA Topics",  value: "2,400+"  },
        ].map(s => (
          <div key={s.label} className="text-center">
            <p className="font-extrabold text-sm leading-tight" style={{ color: "#C9A227" }}>{s.value}</p>
            <p className="text-[9px] font-semibold uppercase tracking-wide mt-0.5" style={{ color: "#5A7050" }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* CTA strip */}
      <div className="px-4 py-2.5 flex items-center justify-between"
        style={{ background: "rgba(0,0,0,0.2)", borderTop: "1px solid rgba(201,162,39,0.12)" }}>
        <span className="text-[11px]" style={{ color: "#7A9160" }}>Free 7-day trial · No credit card</span>
        <button onClick={() => navigate("/login")}
          className="text-[11px] font-extrabold px-3 py-1.5 rounded-lg transition-all"
          style={{ background: "#C9A227", color: "#1A2810" }}>
          Start Preparing →
        </button>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════ */
export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [billing, setBilling] = useState("monthly");
  const [qi, setQi] = useState(0);
  const [sel, setSel] = useState(null);
  const [showExp, setShowExp] = useState(false);
  const [score, setScore] = useState({ c: 0, a: 0 });
  const [gkTab, setGkTab] = useState(0);
  const [faqOpen, setFaqOpen] = useState(null);
  const [showDaily, setShowDaily] = useState(false);

  // ── Live data from DB ──────────────────────────────────────────────────────
  const [news, setNews]           = useState([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [mcqs, setMcqs]           = useState([]);
  const [mcqsLoading, setMcqsLoading] = useState(true);
  const [gkTopics, setGkTopics]   = useState([]);
  const [gkLoading, setGkLoading] = useState(true);
  const [examDates, setExamDates] = useState([]);

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    caApi.getDaily(today)
      .then(d => setNews(Array.isArray(d?.items) ? d.items.slice(0, 6) : []))
      .catch(() => setNews([]))
      .finally(() => setNewsLoading(false));

    gkApi.getRandomMCQs(5)
      .then(data => setMcqs(Array.isArray(data) ? data : []))
      .catch(() => setMcqs([]))
      .finally(() => setMcqsLoading(false));

    gkApi.getFeaturedTopics(3)
      .then(data => setGkTopics(Array.isArray(data) ? data : []))
      .catch(() => setGkTopics([]))
      .finally(() => setGkLoading(false));

    examDatesApi.getUpcoming(6)
      .then(data => setExamDates(Array.isArray(data) ? data : []))
      .catch(() => setExamDates([]));
  }, []);

  useEffect(() => {
    const today = new Date().toDateString();
    if (localStorage.getItem("ep_daily_shown") !== today) {
      const t = setTimeout(() => setShowDaily(true), 900);
      return () => clearTimeout(t);
    }
  }, []);

  function closeDaily() {
    localStorage.setItem("ep_daily_shown", new Date().toDateString());
    setShowDaily(false);
  }

  const Q = mcqs[qi];

  function answer(i) {
    if (sel !== null || !Q) return;
    setSel(i); setShowExp(true);
    setScore(s => ({ c: s.c + (i === Q.ans ? 1 : 0), a: s.a + 1 }));
  }
  function next() {
    if (qi < mcqs.length - 1) { setQi(q => q + 1); setSel(null); setShowExp(false); }
  }
  function reset() { setQi(0); setSel(null); setShowExp(false); setScore({ c: 0, a: 0 }); }

  return (
    <div className="min-h-screen bg-[#F5F6FA] text-gray-800 font-body">
      <SEOHead
        title="EduPulseAI — Free NDA, CDS & CAPF Exam Prep | Syllabus, Current Affairs, GK"
        description="Free NDA 2026 syllabus, CDS syllabus, CAPF syllabus, daily current affairs, complete GK library and mock tests for NDA, CDS, AFCAT and CAPF aspirants. AI-powered defence exam preparation."
        keywords="NDA preparation, CDS preparation, CAPF preparation, AFCAT preparation, defence exam prep, NDA syllabus 2026, CDS syllabus 2026, current affairs for NDA, GK library defence"
        url="/"
        jsonLd={{ "@context": "https://schema.org", "@type": "WebSite", "name": "EduPulseAI", "url": "https://edupulseai.co.in", "description": "Free defence exam preparation platform for NDA, CDS, AFCAT and CAPF aspirants.", "potentialAction": { "@type": "SearchAction", "target": "https://edupulseai.co.in/gk?q={search_term_string}", "query-input": "required name=search_term_string" } }}
      />

      {/* ── Daily fact / quote popup ───────────────────────────── */}
      <AnimatePresence>
        {showDaily && <DailyPopup onClose={closeDaily} />}
      </AnimatePresence>

      {/* ── Upgrade / Pricing Modal ─────────────────────────────── */}
      <AnimatePresence>
        {showUpgrade && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={e => { if (e.target === e.currentTarget) setShowUpgrade(false); }}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.25 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">

              {/* Modal header */}
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
                <div>
                  <h2 className="font-sora font-extrabold text-xl text-gray-900">Choose Your Plan</h2>
                  <p className="text-xs text-gray-400 mt-0.5">Cancel anytime · No hidden charges · Hindi + English</p>
                </div>
                <button onClick={() => setShowUpgrade(false)}
                  className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors">
                  <X size={16} className="text-gray-600" />
                </button>
              </div>

              <div className="p-6">
                {/* Billing toggle */}
                <div className="flex justify-center mb-6">
                  <div className="inline-flex bg-gray-100 rounded-xl p-1">
                    {["monthly", "annually"].map(b => (
                      <button key={b} onClick={() => setBilling(b)}
                        className={`px-6 py-2 rounded-lg text-sm font-bold transition-all ${billing === b ? "bg-[#1B3065] text-white shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                        {b === "monthly" ? "Monthly" : (
                          <span>Annually <span className="ml-1.5 text-[11px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-extrabold">Save 44%</span></span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Plan cards */}
                <div className="grid md:grid-cols-3 gap-4">
                  {PLANS.map((p, i) => (
                    <div key={p.name}
                      className={`relative rounded-2xl border overflow-hidden flex flex-col transition-all ${p.hl ? "border-orange-400 ring-2 ring-orange-400/20 shadow-lg shadow-orange-100" : "border-gray-200 shadow-sm"}`}>
                      {p.badge && (
                        <div className={`py-1.5 text-center text-[11px] font-extrabold tracking-wide ${p.hl ? "bg-orange-500 text-white" : "bg-[#1B3065] text-white"}`}>
                          {p.badge}
                        </div>
                      )}
                      <div className={`p-5 flex-1 flex flex-col ${p.hl ? "bg-orange-50/20" : "bg-white"}`}>
                        <p className="font-sora font-extrabold text-gray-900 text-base mb-0.5">{p.name}</p>
                        <p className="text-gray-400 text-xs mb-4">{p.desc}</p>
                        <div className="mb-4">
                          {p.price === 0
                            ? <p className="font-sora font-black text-3xl text-gray-900">Free</p>
                            : <>
                                <p className="font-sora font-black text-3xl text-gray-900">
                                  ₹{billing === "annually" && p.annual ? Math.round(p.annual / 12) : p.price}
                                  <span className="text-sm font-semibold text-gray-400">/mo</span>
                                </p>
                                {billing === "annually" && p.annual && (
                                  <p className="text-green-600 text-xs mt-0.5 font-semibold">
                                    ₹{p.annual}/year · Save ₹{(p.price * 12) - p.annual}
                                  </p>
                                )}
                              </>
                          }
                        </div>
                        <ul className="space-y-2 mb-5 flex-1">
                          {p.inc.map((h, j) => (
                            <li key={j} className="flex items-start gap-2 text-xs text-gray-700">
                              <CheckCircle size={13} className="text-green-500 flex-shrink-0 mt-0.5" />{h}
                            </li>
                          ))}
                          {p.exc.map((h, j) => (
                            <li key={j} className="flex items-start gap-2 text-xs text-gray-300 line-through">
                              <X size={13} className="flex-shrink-0 mt-0.5" />{h}
                            </li>
                          ))}
                        </ul>
                        <button onClick={() => { setShowUpgrade(false); navigate("/login"); }}
                          className={`w-full py-2.5 rounded-xl text-sm font-extrabold transition-all ${p.hl ? "bg-orange-500 hover:bg-orange-600 text-white shadow-md" : "bg-[#1B3065] hover:bg-[#152554] text-white"}`}>
                          {p.cta}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                <p className="text-center text-xs text-gray-400 mt-5">
                  All plans include 7-day free trial · Secure payment via Razorpay · Cancel anytime
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>


      {/* ── Header ──────────────────────────────────────────────── */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-[62px] gap-4">

            {/* Logo */}
            <a href="/" className="flex items-center gap-2.5 flex-shrink-0">
              <div className="w-10 h-10 bg-[#1B3065] rounded-xl flex items-center justify-center shadow-sm">
                <Shield size={18} className="text-white" />
              </div>
              <div className="leading-tight">
                <p className="font-sora font-extrabold text-[#1B3065] text-[15px] tracking-tight">
                  EduPulse <span className="text-orange-600">Defence</span>
                </p>
                <p className="text-[10px] text-gray-400 font-medium tracking-wide">NDA · CDS · AFCAT · CAPF · SSB</p>
              </div>
            </a>

            {/* Nav */}
            <nav className="hidden lg:flex items-center gap-0.5">
              <a href="#news" className="px-3.5 py-2 text-[13px] font-semibold text-gray-600 hover:text-[#1B3065] hover:bg-blue-50 rounded-lg transition-all">Today's News</a>
              <a href="#practice" className="px-3.5 py-2 text-[13px] font-semibold text-gray-600 hover:text-[#1B3065] hover:bg-blue-50 rounded-lg transition-all">Practice</a>
              <button onClick={() => navigate("/ca")}
                className="px-3.5 py-2 text-[13px] font-bold rounded-lg transition-all flex items-center gap-1.5 hover:bg-red-50"
                style={{ color: "#B91C1C" }}>
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                Current Affairs
              </button>
              <button onClick={() => navigate("/gk")}
                className="px-3.5 py-2 text-[13px] font-bold hover:bg-green-50 rounded-lg transition-all flex items-center gap-1.5"
                style={{ color: "#4A5E2A" }}>
                <BookOpen size={14} /> GK Library
              </button>
              <button onClick={() => navigate("/prep-hub")}
                className="px-3.5 py-2 text-[13px] font-bold hover:bg-indigo-50 rounded-lg transition-all flex items-center gap-1.5"
                style={{ color: "#6366F1" }}>
                🎯 Prep Hub
              </button>
              <div className="relative group">
                <button className="px-3.5 py-2 text-[13px] font-bold hover:bg-amber-50 rounded-lg transition-all flex items-center gap-1.5"
                  style={{ color: "#B45309" }}>
                  📋 Syllabus
                </button>
                <div className="absolute top-full left-0 mt-1 bg-white border border-slate-100 rounded-xl shadow-lg py-1 w-36 z-50 hidden group-hover:block">
                  <button onClick={() => navigate("/syllabus/nda")}
                    className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-amber-50 hover:text-amber-700 transition-colors border-none bg-transparent cursor-pointer">
                    🎖️ NDA Syllabus
                  </button>
                  <button onClick={() => navigate("/syllabus/cds")}
                    className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-blue-50 hover:text-blue-700 transition-colors border-none bg-transparent cursor-pointer">
                    ⚔️ CDS Syllabus
                  </button>
                  <button onClick={() => navigate("/syllabus/capf")}
                    className="w-full text-left px-4 py-2 text-xs font-bold text-slate-700 hover:bg-pink-50 hover:text-pink-700 transition-colors border-none bg-transparent cursor-pointer">
                    🛡️ CAPF (AC) Syllabus
                  </button>
                </div>
              </div>
            </nav>

            <div className="hidden md:flex items-center gap-2 flex-shrink-0">
              <button onClick={() => setShowUpgrade(true)}
                className="text-[13px] font-bold text-orange-600 border-2 border-orange-400 hover:bg-orange-500 hover:text-white hover:border-orange-500 px-4 py-2 rounded-lg transition-all flex items-center gap-1.5">
                <Zap size={13} /> Upgrade
              </button>
              <button onClick={() => navigate("/login")}
                className="text-[13px] font-semibold text-[#1B3065] border-2 border-[#1B3065] hover:bg-[#1B3065] hover:text-white px-4 py-2 rounded-lg transition-all">
                Sign In
              </button>
              <button onClick={() => navigate("/login")}
                className="text-[13px] font-bold bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 rounded-lg transition-all shadow-md shadow-orange-200">
                Start Free →
              </button>
            </div>

            <button className="md:hidden p-2 text-gray-600" onClick={() => setMobileOpen(!mobileOpen)}>
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-gray-100 px-4 py-3 flex flex-col gap-1">
              {["Today's News:#news", "Practice:#practice", "GK Topics:#gk"].map(s => {
                const [l, h] = s.split(":");
                return (
                  <a key={l} href={h} onClick={() => setMobileOpen(false)}
                    className="text-sm font-medium text-gray-700 hover:text-[#1B3065] py-2 px-2 rounded-lg hover:bg-blue-50">
                    {l}
                  </a>
                );
              })}
              <button onClick={() => { setMobileOpen(false); navigate("/ca"); }}
                className="text-sm font-bold text-red-700 py-2 px-2 rounded-lg hover:bg-red-50 w-full text-left flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" /> Current Affairs
              </button>
              <button onClick={() => { setMobileOpen(false); navigate("/gk"); }}
                className="text-sm font-bold py-2 px-2 rounded-lg hover:bg-green-50 w-full text-left flex items-center gap-2"
                style={{ color: "#4A5E2A" }}>
                <BookOpen size={14} /> GK Library
              </button>
              <button onClick={() => { setMobileOpen(false); navigate("/prep-hub"); }}
                className="text-sm font-bold py-2 px-2 rounded-lg hover:bg-indigo-50 w-full text-left flex items-center gap-2"
                style={{ color: "#6366F1" }}>
                🎯 Prep Hub
              </button>
              <button onClick={() => { setMobileOpen(false); navigate("/syllabus/nda"); }}
                className="text-sm font-bold py-2 px-2 rounded-lg hover:bg-amber-50 w-full text-left flex items-center gap-2"
                style={{ color: "#B45309" }}>
                🎖️ NDA Syllabus
              </button>
              <button onClick={() => { setMobileOpen(false); navigate("/syllabus/cds"); }}
                className="text-sm font-bold py-2 px-2 rounded-lg hover:bg-blue-50 w-full text-left flex items-center gap-2"
                style={{ color: "#1D4ED8" }}>
                ⚔️ CDS Syllabus
              </button>
              <button onClick={() => { setMobileOpen(false); navigate("/syllabus/capf"); }}
                className="text-sm font-bold py-2 px-2 rounded-lg hover:bg-pink-50 w-full text-left flex items-center gap-2"
                style={{ color: "#BE185D" }}>
                🛡️ CAPF (AC) Syllabus
              </button>
              <button onClick={() => { setMobileOpen(false); setShowUpgrade(true); }}
                className="text-sm font-bold text-orange-600 border-2 border-orange-400 py-2.5 rounded-lg w-full flex items-center justify-center gap-1.5">
                <Zap size={13} /> Upgrade Plans
              </button>
              <button onClick={() => navigate("/login")}
                className="text-sm font-bold bg-orange-500 text-white py-2.5 rounded-lg w-full">
                Start Free →
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* ── Hero ────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden" style={{ background: "linear-gradient(135deg, #1A2810 0%, #243018 50%, #1E2D12 100%)" }}>
        {/* Subtle diagonal line texture */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "repeating-linear-gradient(45deg, #fff 0, #fff 1px, transparent 0, transparent 50%)", backgroundSize: "20px 20px" }} />
        {/* Gold glow top-right */}
        <div className="absolute top-0 right-0 w-[480px] h-[480px] rounded-full blur-3xl -translate-y-1/3 translate-x-1/4 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(201,162,39,0.18) 0%, transparent 70%)" }} />
        {/* Soft olive glow bottom-left */}
        <div className="absolute bottom-0 left-0 w-[360px] h-[360px] rounded-full blur-3xl translate-y-1/3 -translate-x-1/4 pointer-events-none" style={{ background: "radial-gradient(circle, rgba(74,94,42,0.4) 0%, transparent 70%)" }} />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-16">
          <div className="grid md:grid-cols-5 gap-10 items-center">

            {/* Left: copy */}
            <div className="md:col-span-3">

              {/* Badge */}
              <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                className="inline-flex items-center gap-2 border text-xs font-bold px-4 py-2 rounded-full mb-6"
                style={{ background: "rgba(201,162,39,0.12)", borderColor: "rgba(201,162,39,0.35)", color: "#C9A227" }}>
                <Flag size={12} />
                India's AI-Powered Defence Exam Platform
              </motion.div>

              {/* Headline */}
              <motion.h1 initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.08 }}
                className="font-sora font-black text-[36px] md:text-[52px] leading-[1.1] mb-5 tracking-tight text-white">
                Crack{" "}
                <span className="relative" style={{ color: "#D4AF37" }}>
                  NDA, CDS
                  <svg className="absolute -bottom-1 left-0 w-full" height="4" viewBox="0 0 200 4" preserveAspectRatio="none">
                    <path d="M0 3 Q50 0 100 3 Q150 6 200 3" stroke="#C9A227" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
                  </svg>
                </span>{" "}
                &amp; <span style={{ color: "#D4AF37" }}>AFCAT</span>
                <br className="hidden sm:block" />
                Smarter. Faster.
              </motion.h1>

              {/* Subtext */}
              <motion.p initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.15 }}
                className="text-[15px] leading-relaxed mb-7 max-w-lg" style={{ color: "#A8B89A" }}>
                <span className="font-bold text-white">Daily CA + full GK Library — free, forever.</span>
                {" "}Specialised exam notes, unlimited mock tests &amp; All India Rank for ₹49/month.
              </motion.p>

              {/* Feature highlights */}
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }}
                className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-8">
                {[
                  { icon: Newspaper, text: "Daily CA at 6 AM", sub: "AI-curated from PIB" },
                  { icon: FileText, text: "50,000+ Questions", sub: "NCERT-verified" },
                  { icon: Trophy, text: "All India Rank", sub: "After every test" },
                  { icon: Shield, text: "SSB Prep", sub: "TAT · WAT · GTO · PI" },
                  { icon: Clock, text: "10 Years PYQ", sub: "Fully solved" },
                  { icon: Users, text: "₹49 / month", sub: "Hindi + English" },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-2.5 rounded-xl px-3 py-2.5 transition-all"
                    style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(201,162,39,0.2)" }}>
                    <f.icon size={15} className="flex-shrink-0 mt-0.5" style={{ color: "#C9A227" }} />
                    <div>
                      <p className="text-white text-[12px] font-bold leading-tight">{f.text}</p>
                      <p className="text-[11px] leading-tight mt-0.5" style={{ color: "#7A9160" }}>{f.sub}</p>
                    </div>
                  </div>
                ))}
              </motion.div>

              {/* CTAs */}
              <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.26 }}
                className="flex flex-wrap gap-3 items-center">
                <button onClick={() => navigate("/login")}
                  className="flex items-center gap-2 font-extrabold px-8 py-3.5 rounded-xl transition-all text-sm shadow-lg"
                  style={{ background: "#C9A227", color: "#1A2810", boxShadow: "0 8px 24px rgba(201,162,39,0.35)" }}
                  onMouseEnter={e => e.currentTarget.style.background = "#B8920F"}
                  onMouseLeave={e => e.currentTarget.style.background = "#C9A227"}>
                  Start Free — No Card Needed <ArrowRight size={15} />
                </button>
                <a href="#news"
                  className="flex items-center gap-2 font-semibold px-6 py-3.5 rounded-xl transition-all text-sm text-white"
                  style={{ background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.15)" }}>
                  See Today's News ↓
                </a>
              </motion.div>

              {/* Social proof */}
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5, delay: 0.35 }}
                className="flex items-center gap-3 mt-6 pt-6" style={{ borderTop: "1px solid rgba(201,162,39,0.2)" }}>
                <div className="flex -space-x-2">
                  {[0,1,2,3,4].map(i => (
                    <div key={i} className="w-7 h-7 rounded-full flex items-center justify-center"
                      style={{ background: "linear-gradient(135deg,#3B4A2A,#4A5E2A)", border: "2px solid #C9A227" }}>
                      <User size={13} style={{ color: "#C9A227" }} />
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-white text-xs font-bold">10,000+ aspirants already preparing</p>
                  <p className="text-[11px]" style={{ color: "#7A9160" }}>Free 7-day trial · No credit card</p>
                </div>
              </motion.div>
            </div>

            {/* Right: Exam Countdown + Stats card */}
            <motion.div className="md:col-span-2"
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.55, delay: 0.2 }}>
              <ExamCountdownCard navigate={navigate} />

              {/* Exam pills */}
              <div className="flex flex-wrap gap-2 mt-4 justify-center">
                {["NDA","CDS","AFCAT","CAPF","SSB","TA","MNS"].map(n => (
                  <span key={n} className="text-[11px] font-extrabold px-3 py-1 rounded-lg"
                    style={{ background: "rgba(201,162,39,0.15)", color: "#C9A227", border: "1px solid rgba(201,162,39,0.3)" }}>{n}</span>
                ))}
              </div>
            </motion.div>

          </div>
        </div>

        {/* Bottom stat bar */}
        <div style={{ borderTop: "1px solid rgba(201,162,39,0.15)", background: "rgba(0,0,0,0.2)" }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-0"
              style={{ "--divide-color": "rgba(201,162,39,0.15)" }}>
              {[
                { val: "10,000+", lbl: "Active Aspirants", icon: Users },
                { val: "50,000+", lbl: "Questions in Bank", icon: FileText },
                { val: "365", lbl: "CA Digests Published", icon: Newspaper },
                { val: "₹49/mo", lbl: "Full Access Price", icon: Trophy },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-3 md:px-8 first:md:pl-0 last:md:pr-0"
                  style={{ borderRight: i < 3 ? "1px solid rgba(201,162,39,0.15)" : "none" }}>
                  <s.icon size={18} className="flex-shrink-0" style={{ color: "#C9A227" }} />
                  <div>
                    <p className="font-sora font-black text-lg text-white leading-none">{s.val}</p>
                    <p className="text-[11px] mt-0.5" style={{ color: "#7A9160" }}>{s.lbl}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Main layout ─────────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid lg:grid-cols-[1fr_320px] gap-6">

          {/* LEFT ─────────────────────────────────────────────── */}
          <div className="space-y-10">

            {/* ── TODAY'S NEWS ─────────────────────────────────── */}
            <section id="news">
              <div className="flex items-center justify-between mb-1">
                <SectionHead label="Live · Updated at 6 AM daily" title="Today's Defence News" />
                <div className="flex items-center gap-1.5 text-xs text-gray-400 bg-white border border-gray-200 px-3 py-1.5 rounded-full shadow-sm flex-shrink-0 mb-5">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse inline-block" />
                  {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </div>
              </div>

              {/* 2-column news grid */}
              {newsLoading ? (
                <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-gray-300" /></div>
              ) : news.length === 0 ? (
                <div className="text-center py-10 text-sm text-gray-400">No articles yet — check back after 7 AM IST</div>
              ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                {news.map((n, i) => {
                  const svc = getCaStyle(n.category);
                  return (
                  <FadeIn key={n._id || i} delay={i * 0.05}>
                    <div onClick={() => navigate(`/ca/${n.slug}`)}
                      className="rounded-xl p-4 hover:shadow-md transition-all group cursor-pointer h-full flex flex-col"
                      style={{ background: svc.cardBg, border: `1px solid ${svc.border}40`, borderLeft: `4px solid ${svc.border}` }}>
                      <div className="flex items-center gap-2 mb-2.5">
                        <span className="flex-shrink-0 w-6 h-6 rounded-lg text-[11px] font-extrabold flex items-center justify-center font-sora"
                          style={{ background: svc.border, color: "#fff" }}>
                          {i + 1}
                        </span>
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded"
                          style={{ background: `${svc.border}18`, color: svc.border }}>
                          {n.category}
                        </span>
                        {n.importance === "high" && (
                          <span className="flex items-center gap-0.5 text-[11px] font-bold text-red-500 ml-auto">
                            <Flame size={10} />HOT
                          </span>
                        )}
                      </div>
                      <h3 className="font-sora font-bold text-[13px] text-gray-900 leading-snug mb-1.5 flex-1">
                        {n.title}
                      </h3>
                      <p className="text-gray-500 text-xs leading-relaxed mb-3 line-clamp-2">{n.summary}</p>
                      <div className="flex items-center gap-2 text-[11px] text-gray-400 mt-auto pt-2 border-t border-gray-200">
                        <span className="font-bold flex items-center gap-1" style={{ color: svc.border }}>
                          <svc.Icon size={10} />{svc.label}
                        </span>
                        {n.sourceName && <><span>·</span><span>{n.sourceName}</span></>}
                      </div>
                    </div>
                  </FadeIn>
                  );
                })}
              </div>
              )}

              {/* CTA strip */}
              <FadeIn delay={0.3}>
                <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 bg-gradient-to-r from-blue-50 to-orange-50 border border-blue-200 rounded-xl px-5 py-4">
                  <p className="text-sm text-gray-700 flex items-center gap-2">
                    <Lock size={14} className="text-blue-600 flex-shrink-0" />
                    Get <strong className="text-[#1B3065]">full daily digest + 8 MCQs</strong> every morning at 6 AM — free with signup
                  </p>
                  <button onClick={() => navigate("/login")}
                    className="flex-shrink-0 text-sm font-bold bg-[#1B3065] hover:bg-[#152554] text-white px-5 py-2 rounded-lg transition-all shadow-sm">
                    Get Daily CA Free →
                  </button>
                </div>
              </FadeIn>
            </section>

            {/* ── PRACTICE MCQ ─────────────────────────────────── */}
            <section id="practice">
              <div className="flex items-center justify-between">
                <SectionHead label="Free Practice · No Signup Required" title="Practice Question" color="bg-green-600" />
                {score.a > 0 && (
                  <div className="mb-5 bg-white border border-gray-200 rounded-xl px-4 py-2 text-sm shadow-sm">
                    Score <span className="font-bold text-green-600">{score.c}/{score.a}</span>
                  </div>
                )}
              </div>

              {mcqsLoading ? (
                <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-gray-300" /></div>
              ) : mcqs.length === 0 ? (
                <div className="text-center py-10 text-sm text-gray-400">No questions available yet</div>
              ) : Q ? (
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                {/* Progress bar */}
                <div className="h-1 bg-gray-100">
                  <div className="h-full bg-gradient-to-r from-green-500 to-green-400 transition-all duration-500"
                    style={{ width: `${((qi + (sel !== null ? 1 : 0)) / mcqs.length) * 100}%` }} />
                </div>

                {/* Q header */}
                <div className="px-5 py-4 border-b border-gray-100 bg-gray-50/60">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold px-2.5 py-1 rounded-lg border text-blue-600 bg-blue-50 border-blue-200">
                        {Q.categoryLabel || Q.category || "GK"}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400 font-medium">{qi + 1} / {mcqs.length}</span>
                  </div>
                  <p className="font-sora font-bold text-gray-900 text-[15px] leading-snug">{Q.q}</p>
                </div>

                {/* Options */}
                <div className="p-5 grid sm:grid-cols-2 gap-2.5">
                  {(Q.opts || []).map((opt, i) => {
                    const isSelected = sel === i;
                    const isCorrect  = i === Q.ans;
                    const revealed   = sel !== null;
                    let cls = "border-gray-200 bg-white hover:border-blue-400 hover:bg-blue-50/50 cursor-pointer";
                    let num = "bg-gray-100 text-gray-500";
                    if (revealed) {
                      if (isCorrect)          { cls = "border-green-400 bg-green-50 cursor-default"; num = "bg-green-500 text-white"; }
                      else if (isSelected)    { cls = "border-red-400 bg-red-50 cursor-default"; num = "bg-red-500 text-white"; }
                      else                    { cls = "border-gray-100 bg-gray-50/60 opacity-50 cursor-default"; }
                    }
                    return (
                      <button key={i} onClick={() => answer(i)} disabled={sel !== null}
                        className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-left text-sm font-medium transition-all ${cls}`}>
                        <span className={`w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center text-xs font-extrabold transition-all ${num}`}>
                          {["A","B","C","D"][i]}
                        </span>
                        <span className="text-gray-800 leading-snug">{opt}</span>
                        {revealed && isCorrect && <CheckCircle size={14} className="text-green-500 ml-auto flex-shrink-0" />}
                      </button>
                    );
                  })}
                </div>

                {/* Explanation */}
                <AnimatePresence>
                  {showExp && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                      <div className="mx-5 mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
                        <p className="text-[11px] font-extrabold text-amber-700 uppercase tracking-widest mb-1.5">📝 Explanation</p>
                        <p className="text-sm text-gray-700 leading-relaxed">{Q.exp}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Footer */}
                <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between gap-3">
                  {qi < mcqs.length - 1 ? (
                    <button onClick={next} disabled={sel === null}
                      className={`flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-lg transition-all ${sel !== null ? "bg-[#1B3065] hover:bg-[#152554] text-white shadow-sm" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}>
                      Next <ChevronRight size={14} />
                    </button>
                  ) : (
                    <button onClick={reset}
                      className="flex items-center gap-2 text-sm font-bold bg-[#1B3065] hover:bg-[#152554] text-white px-5 py-2.5 rounded-lg shadow-sm">
                      <RefreshCw size={13} /> Try Again
                    </button>
                  )}
                  {sel === null
                    ? <span className="text-xs text-gray-400">Select an option to reveal the answer</span>
                    : <button onClick={() => navigate("/login")} className="text-xs font-bold text-orange-600 hover:underline flex items-center gap-1">
                        Unlock 10,000+ questions <ArrowRight size={11} />
                      </button>
                  }
                </div>
              </div>
              ) : null}
            </section>

            {/* ── GK TOPICS ────────────────────────────────────── */}
            <section id="gk">
              <SectionHead label="NCERT + Defence Specific" title="GK Topic of the Day" color="bg-purple-600" />

              {gkLoading ? (
                <div className="flex justify-center py-10"><Loader2 size={24} className="animate-spin text-gray-300" /></div>
              ) : gkTopics.length === 0 ? (
                <div className="text-center py-10 text-sm text-gray-400">GK topics loading soon</div>
              ) : (
              <>
              {/* Tabs */}
              <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
                {gkTopics.map((t, i) => {
                  const sty = getCaStyle(t.category);
                  return (
                  <button key={i} onClick={() => setGkTab(i)}
                    className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-semibold transition-all ${gkTab === i ? "bg-[#1B3065] border-[#1B3065] text-white shadow-sm" : "bg-white border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-800"}`}>
                    <sty.Icon size={14} /> <span className="hidden sm:inline">{t.title}</span>
                  </button>
                  );
                })}
              </div>

              <AnimatePresence mode="wait">
                {(() => {
                  const t = gkTopics[gkTab];
                  if (!t) return null;
                  const sty = getCaStyle(t.category);
                  const ActiveIcon = sty.Icon;
                  return (
                  <motion.div key={gkTab}
                    initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                    <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                      {/* Topic header */}
                      <div className="px-5 py-4 bg-gray-50/80 border-b border-gray-100"
                        style={{ borderLeft: `4px solid ${sty.border}` }}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                            style={{ background: `${sty.border}18` }}>
                            <ActiveIcon size={22} style={{ color: sty.border }} />
                          </div>
                          <div>
                            <h3 className="font-sora font-extrabold text-gray-900 text-lg">{t.title}</h3>
                            <p className="text-gray-500 text-xs">{t.categoryLabel || t.category}</p>
                          </div>
                        </div>
                      </div>

                      <div className="p-5">
                        <p className="text-[11px] font-extrabold text-gray-400 uppercase tracking-widest mb-3">Key Facts</p>
                        <div className="grid sm:grid-cols-2 gap-2 mb-5">
                          {(t.facts || []).slice(0, 6).map((f, i) => (
                            <motion.div key={i}
                              initial={{ opacity: 0, x: -5 }} animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: i * 0.04 }}
                              className="flex items-start gap-2.5">
                              <span className="flex-shrink-0 w-5 h-5 rounded-lg flex items-center justify-center text-[11px] font-extrabold mt-0.5 bg-gray-100 text-gray-600">
                                {i + 1}
                              </span>
                              <p className="text-sm text-gray-700 leading-relaxed">{f.fact || f}</p>
                            </motion.div>
                          ))}
                        </div>
                      </div>

                      <div className="bg-gray-50 border-t border-gray-100 px-5 py-3 flex items-center justify-between">
                        <span className="text-xs text-gray-500 flex items-center gap-1.5">
                          <Lock size={11} /> 500+ GK topics with MCQs — unlock after free signup
                        </span>
                        <button onClick={() => navigate(`/gk/${t.category}/${t.slug}`)}
                          className="text-xs font-bold text-orange-600 hover:underline">Read More →</button>
                      </div>
                    </div>
                  </motion.div>
                  );
                })()}
              </AnimatePresence>
              </>
              )}
            </section>


          </div>{/* end LEFT */}

          {/* RIGHT SIDEBAR ──────────────────────────────────────── */}
          <div className="space-y-4">

            {/* Signup */}
            <div className="bg-[#1B3065] text-white rounded-2xl overflow-hidden shadow-lg">
              <div className="bg-orange-500 px-5 py-2.5">
                <p className="text-xs font-extrabold uppercase tracking-widest flex items-center gap-1.5"><Target size={11} /> Free. No Credit Card. Ever.</p>
              </div>
              <div className="p-5">
                <div className="space-y-1.5 mb-4">
                  {[
                    { label: "Daily CA digest", free: true },
                    { label: "Full GK Library", free: true },
                    { label: "5 mock tests/month", free: true },
                    { label: "Specialised exam notes", free: false },
                    { label: "Unlimited mock tests + AIR", free: false },
                    { label: "SSB prep + PYQ papers", free: false },
                  ].map(f => (
                    <div key={f.label} className="flex items-center gap-2 text-[11px]">
                      {f.free
                        ? <CheckCircle size={11} className="text-green-400 flex-shrink-0" />
                        : <span className="text-[10px] font-extrabold px-1.5 py-0.5 rounded flex-shrink-0" style={{ background: "#C9A227", color: "#1A2810" }}>₹49</span>
                      }
                      <span className={f.free ? "text-blue-200" : "text-blue-300/70"}>{f.label}</span>
                    </div>
                  ))}
                </div>
                <button onClick={() => navigate("/login")}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-extrabold py-3 rounded-xl text-sm transition-all mb-2 shadow-md">
                  Create Free Account →
                </button>
                <button onClick={() => navigate("/login")}
                  className="w-full bg-white/10 hover:bg-white/20 border border-white/10 text-white font-semibold py-2.5 rounded-xl text-sm transition-all">
                  Sign In
                </button>
              </div>
            </div>

            {/* Exam countdown — hidden until exam dates are confirmed */}

            {/* Why us */}
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden shadow-sm">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center gap-2">
                <div className="w-1 h-4 bg-green-500 rounded-full" />
                <p className="font-sora font-extrabold text-sm text-gray-800">Why Aspirants Choose Us</p>
              </div>
              <div className="px-4 py-3 space-y-2.5">
                {WHY.map((w, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-sm text-gray-700">
                    <CheckCircle size={13} className="text-green-500 flex-shrink-0" />
                    {w}
                  </div>
                ))}
              </div>
            </div>

            {/* Testimonial */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 shadow-sm">
              <div className="flex gap-0.5 mb-3">
                {[...Array(5)].map((_, i) => <Star key={i} size={13} className="fill-yellow-400 text-yellow-400" />)}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed italic mb-3">
                "The daily CA digest made the real difference. 6 months of defence news without reading newspapers. Cleared NDA written in first attempt."
              </p>
              <div className="pt-3 border-t border-gray-100">
                <p className="font-sora font-bold text-sm text-gray-900">Arjun Singh</p>
                <p className="text-xs text-orange-600 font-semibold">NDA 149th Course · Lucknow, UP</p>
              </div>
            </div>

            {/* App download */}
            <div className="bg-gradient-to-br from-[#1B3065] to-[#152554] text-white rounded-2xl p-4 shadow-lg">
              <p className="font-sora font-bold text-sm mb-1">Study on Mobile</p>
              <p className="text-blue-300 text-xs mb-4 leading-relaxed">Download our app — offline access, daily CA alerts, practice on the go.</p>
              <div className="flex gap-2">
                <button className="flex-1 bg-white/10 hover:bg-white/20 border border-white/15 text-xs font-bold py-2.5 rounded-xl transition-all">
                  <Smartphone size={12} className="inline mr-1" /> Android App
                </button>
                <button className="flex-1 bg-white/10 hover:bg-white/20 border border-white/15 text-xs font-bold py-2.5 rounded-xl transition-all">
                  <Smartphone size={12} className="inline mr-1" /> iOS App
                </button>
              </div>
            </div>

          </div>{/* end SIDEBAR */}
        </div>
      </div>

      {/* ── Final CTA ───────────────────────────────────────────── */}
      <section className="bg-[#1B3065] text-white mt-12 py-14 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="w-14 h-14 bg-orange-500/15 border border-orange-400/25 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Shield size={24} className="text-orange-400" />
          </div>
          <h2 className="font-sora font-black text-2xl md:text-3xl mb-3">
            Your Commission Awaits.<br />Start Preparing Today.
          </h2>
          <p className="text-blue-300 text-sm leading-relaxed mb-7 max-w-md mx-auto">
            Join thousands of aspirants already preparing smarter. Free to start — no credit card, no commitment.
          </p>
          <button onClick={() => navigate("/login")}
            className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-extrabold px-10 py-3.5 rounded-xl transition-all shadow-xl shadow-orange-900/30 text-base">
            Start Free Now <ArrowRight size={16} />
          </button>
          <p className="text-blue-400 text-xs mt-4">No credit card · Cancel anytime · Hindi + English</p>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <footer className="bg-gray-900 text-gray-400 py-10 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-[#1B3065] rounded-xl flex items-center justify-center">
                  <Shield size={14} className="text-white" />
                </div>
                <span className="font-sora font-extrabold text-white text-sm">EduPulse Defence</span>
              </div>
              <p className="text-xs leading-relaxed text-gray-500 mb-3">
                India's AI-powered defence exam preparation platform. NDA, CDS, AFCAT, CAPF and SSB.
              </p>
            </div>
            <div>
              <p className="font-sora font-bold text-white text-sm mb-3">Exams</p>
              {["NDA Preparation","CDS Preparation","AFCAT Preparation","CAPF Preparation","SSB Coaching","Territorial Army"].map(e => (
                <p key={e} className="text-xs mb-1.5 hover:text-white cursor-pointer transition-colors">{e}</p>
              ))}
            </div>
            <div>
              <p className="font-sora font-bold text-white text-sm mb-3">Resources</p>
              {["Daily Current Affairs","GK Library","Previous Year Papers","Mock Tests","SSB Practice","Study Material"].map(r => (
                <p key={r} className="text-xs mb-1.5 hover:text-white cursor-pointer transition-colors">{r}</p>
              ))}
            </div>
            <div>
              <p className="font-sora font-bold text-white text-sm mb-3">Company</p>
              {["About Us","Contact","Privacy Policy","Terms of Use","Refund Policy","Careers"].map(c => (
                <p key={c} className="text-xs mb-1.5 hover:text-white cursor-pointer transition-colors">{c}</p>
              ))}
            </div>
          </div>
          {/* FAQ accordion */}
          <div className="border-t border-gray-800 pt-8 mb-8">
            <p className="font-sora font-bold text-white text-sm mb-4">Frequently Asked Questions</p>
            <div className="grid md:grid-cols-2 gap-2">
              {FAQS.map((f, i) => (
                <div key={i} className="border border-gray-700 rounded-xl overflow-hidden">
                  <button onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                    className="w-full flex items-center justify-between px-4 py-3 text-left gap-3 hover:bg-gray-800 transition-colors">
                    <span className="text-xs font-semibold text-gray-300">{f.q}</span>
                    <ChevronDown size={13} className={`flex-shrink-0 text-gray-500 transition-transform duration-200 ${faqOpen === i ? "rotate-180" : ""}`} />
                  </button>
                  <AnimatePresence>
                    {faqOpen === i && (
                      <motion.div initial={{ height: 0 }} animate={{ height: "auto" }}
                        exit={{ height: 0 }} className="overflow-hidden">
                        <p className="px-4 pb-3 text-xs text-gray-500 leading-relaxed border-t border-gray-700 pt-2">{f.a}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-800 pt-5 flex flex-col md:flex-row items-center justify-between gap-2">
            <p className="text-xs text-gray-600">© {new Date().getFullYear()} EduPulse Defence. All rights reserved.</p>
            <p className="text-xs text-gray-600">NDA · CDS · AFCAT · CAPF · SSB · Territorial Army · MNS</p>
          </div>
        </div>
      </footer>

    </div>
  );
}
